// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@hardwoods/App/Store/Reducers";
import { constants } from "@hardwoods/XDL/constants";
import CaretDownSolid from "@hardwoods/XDL/Icons/CaretDownSolid";
import CaretUpSolid from "@hardwoods/XDL/Icons/CaretUpSolid";
import { ellipsis } from "@hardwoods/XDL/Utilities";
import color from "@hardwoods/XDL/Utilities/color";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, MouseEventHandler } from "react";
import { css } from "styled-components";

export const headerIconSize = {
    default: 20,
    sticky: 24,
};

interface HeaderPopupLinkProps {
    showIcon?: boolean;
    showLabel?: boolean;
    isOpened?: boolean;
    icon?: React.ComponentType | string;
    onClick?: MouseEventHandler<any>;
    iconProps?: { type?: "signIn" | "miniCart"; [key: string]: any };
    titleProps?: { [key: string]: any };
    clickableProps?: { [key: string]: any };
}

interface HeaderPopupLinkStyle {
    wrapper?: InjectableCss;
    clickable?: ClickablePresentationProps;
    icon?: IconPresentationProps;
    title?: TypographyProps;
    arrowIconWrapper?: IconPresentationProps;
}

const styles: HeaderPopupLinkStyle = {
    wrapper: {
        css: css`
            display: flex;
            align-items: center;
            padding: 8px;
            min-height: ${({ isSticky }: any) =>
                isSticky ? constants.header.size.sticky : constants.header.size.default}px;
            background: ${({ isOpened }: any) => (isOpened ? color("primary.dark") : "transparent")};
            box-shadow:  0 6px 0 0 ${color("primary.dark")}};

            ${({ isOpened }: any) => (!isOpened ? `box-shadow: none;` : "")};
            ${xScreen.lte("sm")} {
                background: transparent;
            }
        `,
    },
    clickable: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: flex-start;
        `,
    },
    icon: {
        color: "common.white",
        css: css`
            margin-bottom: 0;
            border-radius: 50%;
            ${({ isOpened, isSticky, type }: any) => css`
                ${type === "signIn" &&
                css`
                    background: ${isOpened ? color("common.white") : color("secondary")};
                    color: ${isOpened ? color("primary.dark") : color("common.white")};
                    padding: ${isSticky ? 7 : 6}px;
                    ${xScreen.lte("sm")} {
                        background: ${isOpened ? color("primary.dark") : color("secondary")};
                        color: ${color("common.white")};
                    }
                `}
                ${type === "miniCart" &&
                css`
                    color: ${isOpened ? color("common.white") : color("secondary")};
                    ${xScreen.lte("sm")} {
                        color: ${isOpened ? color("primary.dark") : color("secondary")};
                    }
                `}
            `}
        `,
    },
    title: {
        transform: "capitalize",
        css: css`
            display: flex;
            flex-direction: column;
            margin-left: 10px;
            max-width: 150px;

            color: ${({ isOpened }: any) => (isOpened ? color("common.white") : color("common.ui14"))};
            font-size: 14px;
            line-height: 22px;
            letter-spacing: 0;
            font-weight: bold;

            > .sub-title {
                display: block;
                font-size: 12px;
                line-height: 18px;
                font-weight: normal;
            }

            ${({ isLoading }: any) =>
                isLoading &&
                css`
                    ${xScreen.gt("sm")} {
                        min-width: 1.25em;
                        display: inline-flex;

                        // Draw 3 dots with animation
                        &::after {
                            overflow: hidden;
                            display: inline-block;
                            vertical-align: bottom;
                            animation: ellipsis steps(4, end) 900ms infinite 1s;
                            content: "\\2026";
                            width: 0;
                        }

                        @keyframes ellipsis {
                            to {
                                width: 1.25em;
                            }
                        }
                    }
                `}

            ${xScreen.lte("md")} {
                display: none;
            }
            ${({ sticky }: any) => sticky && `display: none;`}
        `,
    },
    arrowIconWrapper: {
        size: 14,
        css: css`
            margin-left: 10px;
        `,
    },
};

const HeaderPopupLink: FC<HeaderPopupLinkProps> = ({
    iconProps,
    titleProps,
    clickableProps,
    showIcon,
    showLabel,
    isOpened,
    icon,
    onClick,
    children,
}) => {
    const isSticky = useAppSelector(state => (state as CustomAppState).xHeader.isSticky);

    const props = { isOpened, isSticky };
    return (
        <StyledWrapper {...styles.wrapper} {...props}>
            <Clickable {...styles.clickable} onClick={onClick} {...clickableProps}>
                {showIcon && (
                    <Icon
                        {...styles.icon}
                        {...props}
                        {...iconProps}
                        size={isSticky ? headerIconSize.sticky : headerIconSize.default}
                        src={icon}
                    />
                )}
                {showLabel && (
                    <Typography {...styles.title} {...props} {...titleProps}>
                        {children}
                    </Typography>
                )}
                {!isSticky && (
                    <Hidden below="lg">
                        {isOpened && <IconMemo src={CaretUpSolid} color="text.inverse" {...styles.arrowIconWrapper} />}
                        {!isOpened && <IconMemo src={CaretDownSolid} color="secondary" {...styles.arrowIconWrapper} />}
                    </Hidden>
                )}
            </Clickable>
        </StyledWrapper>
    );
};

HeaderPopupLink.defaultProps = {
    showLabel: true,
    showIcon: true,
};

export default HeaderPopupLink;
